import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, Routes, Route, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import clsx from 'clsx';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import { FORMA_LINK_BTN } from '../../utils/listPageUi';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  BuildingLibraryIcon,
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import localStorageService from '../../utils/localStorageService';
import toast from 'react-hot-toast';

const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const hours = Array.from({ length: 12 }, (_, i) => 8 + i); // 8h à 19h

// Composant de détail d'un événement
const ScheduleEventDetail = ({ event, onClose, onDelete, onEdit, onNavigate }) => {
  if (!event) return null;

  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button 
            onClick={onClose} 
            className="rounded-xl border border-slate-200 bg-white p-2 text-slate-700 shadow-soft transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900/75 dark:text-slate-200 dark:hover:bg-slate-800/80 mr-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50">
            Détails du cours
          </h2>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => onEdit(event)}
            className="bg-yellow-500 text-slate-900 dark:text-slate-50 px-3 py-2 rounded-lg hover:bg-yellow-600 transition-colors flex items-center space-x-1"
          >
            <PencilIcon className="h-4 w-4" />
            <span>Modifier</span>
          </button>
          <button
            onClick={() => onDelete(event.id)}
            className="bg-red-500 text-slate-900 dark:text-slate-50 px-3 py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center space-x-1"
          >
            <TrashIcon className="h-4 w-4" />
            <span>Supprimer</span>
          </button>
        </div>
      </div>

      <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 mb-4 border-l-4" style={{ borderLeftColor: event.color }}>
        <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50">{event.title}</h3>
      </div>
      
      <div className="space-y-4">
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 flex items-center">
          <CalendarDaysIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-3" />
          <div>
            <div className="text-slate-600 dark:text-slate-400 text-sm">Horaires</div>
            <div className="text-slate-900 dark:text-slate-50">
              {days[event.day]}, {event.startHour}:00 - {event.endHour}:00
            </div>
          </div>
        </div>
        
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center">
          <UserGroupIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-3" />
          <div>
            <div className="text-slate-600 dark:text-slate-400 text-sm">Formateur</div>
            <div className="text-slate-900 dark:text-slate-50">
              {event.trainerName}
            </div>
          </div>
          </div>
          <button
            onClick={() => onNavigate(event, 'trainer')}
            className="text-forma-600 dark:text-forma-400 hover:text-forma-500 dark:hover:text-forma-300 text-sm underline"
          >
            Voir page formateurs
          </button>
        </div>
        
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center">
          <BuildingLibraryIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-3" />
          <div>
            <div className="text-slate-600 dark:text-slate-400 text-sm">Salle</div>
            <div className="text-slate-900 dark:text-slate-50">
              {event.classroomName}
            </div>
              {event.capacity && (
                <div className="text-slate-400 dark:text-slate-500 text-xs">
                  Capacité: {event.capacity} places
          </div>
              )}
            </div>
          </div>
          <button
            onClick={() => onNavigate(event, 'classroom')}
            className="text-forma-600 dark:text-forma-400 hover:text-forma-500 dark:hover:text-forma-300 text-sm underline"
          >
            Voir page salles
          </button>
        </div>

        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center">
            <AcademicCapIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-3" />
            <div>
              <div className="text-slate-600 dark:text-slate-400 text-sm">Cours</div>
              <div className="text-slate-900 dark:text-slate-50">
                {event.courseName}
              </div>
              {event.courseCode && (
                <div className="text-slate-400 dark:text-slate-500 text-xs">
                  Code: {event.courseCode}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => onNavigate(event, 'course')}
            className="text-forma-600 dark:text-forma-400 hover:text-forma-500 dark:hover:text-forma-300 text-sm underline"
          >
            Voir page cours
          </button>
        </div>
      </div>
    </div>
  );
};

// Wrapper component to correctly use the useParams hook
const ScheduleEventDetailWrapper = ({ events, onDelete, onEdit, onNavigate }) => {
  const navigate = useNavigate();
  const { id } = useParams(); // useParams hook used at the top level of the component
  const event = events.find(e => e.id === id);
  
  return (
    <ScheduleEventDetail 
      event={event}
      onClose={() => navigate('/schedule')}
      onDelete={(id) => {
        onDelete(id);
        navigate('/schedule');
      }}
      onEdit={onEdit}
      onNavigate={onNavigate}
    />
  );
};

const SchedulePage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [activeTab, setActiveTab] = useState('week'); // 'week', 'day', 'trainers', 'courses', 'rooms'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'trainer', 'classroom'

  // Fetch real-time data from all interconnected pages
  const { data: trainingSessions = [], isLoading: sessionsLoading } = useQuery('training-sessions', () => {
    console.log('Fetching training sessions for schedule...');
    // No mock data - using only real sessions
    return localStorageService.getAllItems('training-sessions');
  }, {
    staleTime: 0,
    cacheTime: 0,
  });

  const { data: coursesData = [] } = useQuery('courses', () => {
    console.log('Fetching courses for schedule...');
    return localStorageService.getAllItems('courses');
  }, {
    staleTime: 0,
    cacheTime: 0,
  });

  const { data: classroomsData = [] } = useQuery('classrooms', () => {
    console.log('Fetching classrooms for schedule...');
    return localStorageService.getAllItems('classrooms');
  }, {
    staleTime: 0,
    cacheTime: 0,
  });

  const { data: trainersData = [] } = useQuery('trainers', () => {
    console.log('Fetching trainers for schedule...');
    return localStorageService.getAllItems('trainers');
  }, {
    staleTime: 0,
    cacheTime: 0,
  });

  const { data: batchesData = [] } = useQuery('batches', () => {
    console.log('Fetching batches for schedule...');
    return localStorageService.getAllItems('batches');
  }, {
    staleTime: 0,
    cacheTime: 0,
  });

  // Calendar navigation functions
  const goToPreviousMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    setCurrentDate(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    setCurrentDate(newDate);
  };

  const goToPreviousYear = () => {
    const newDate = new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), 1);
    setCurrentDate(newDate);
  };

  const goToNextYear = () => {
    const newDate = new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), 1);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Check if a date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Check if date is in current month
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth() && 
           date.getFullYear() === currentDate.getFullYear();
  };

  // Get current month and year for display
  const getCurrentMonthYear = () => {
    return currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  // Generate calendar grid (6 weeks x 7 days = 42 days)
  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // First day of the month
    const firstDayOfMonth = new Date(year, month, 1);
    // Last day of the month
    const lastDayOfMonth = new Date(year, month + 1, 0);
    
    // First day to show on calendar (might be from previous month)
    const firstCalendarDay = new Date(firstDayOfMonth);
    firstCalendarDay.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay());
    
    // Generate 42 days (6 weeks x 7 days)
    const calendarDays = [];
    const currentDay = new Date(firstCalendarDay);
    
    for (let i = 0; i < 42; i++) {
      calendarDays.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    return calendarDays;
  };

  // Week view helpers
  const getCurrentWeekDates = () => {
    const startOfWeek = new Date(currentDate);
    const dayOfWeek = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - dayOfWeek;
    startOfWeek.setDate(diff);
    
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(startOfWeek);
      dayDate.setDate(startOfWeek.getDate() + i);
      weekDays.push(dayDate);
    }
    return weekDays;
  };


  // Fonction pour formater la date en français
  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Get event color based on category
  const getEventColor = (category) => {
    const colorMap = {
      'dev': '#3B82F6',           // Blue for development
      'design': '#8B5CF6',        // Purple for design
      'sys': '#10B981',           // Green for systems
      'data': '#6366F1',          // Indigo for data science
      'security': '#DC2626',      // Red for security/cybersecurity
      'cloud': '#7C3AED',         // Violet for cloud
      'formation': '#F59E0B',     // Orange for general formation
      'default': '#6B7280'        // Gray for unknown categories
    };
    
    return colorMap[category] || colorMap['default'];
  };

  // Convert batches to schedule events (primary source)
  const batchEvents = (Array.isArray(batchesData) ? batchesData : []).flatMap(batch => {
    // Find related data (with defensive checks)
    const course = (Array.isArray(coursesData) ? coursesData : []).find(c => c.id === batch.courseId);
    const classroom = (Array.isArray(classroomsData) ? classroomsData : []).find(c => c.id === batch.room);
    const trainer = (Array.isArray(trainersData) ? trainersData : []).find(t => t.id === batch.trainerId);

    // Create events for each scheduled day
    return (batch.days || []).map(dayIndex => {
      const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
      
      return {
        id: `${batch.id}-${dayIndex}`,
        batchId: batch.id,
        batchName: batch.name,
        title: `${course?.title || 'Cours'} - ${batch.name}`,
        courseId: batch.courseId,
        courseName: course?.title || 'Cours non spécifié',
        courseCode: course?.courseCode || '',
        trainerId: batch.trainerId,
        trainerName: trainer?.name || 'Formateur non spécifié',
        classroomId: batch.room,
        classroomName: classroom?.name || 'Salle non spécifiée',
        startDate: batch.startDate,
        endDate: batch.endDate,
        startTime: batch.startTime,
        endTime: batch.endTime,
        day: dayIndex,
        startHour: parseInt(batch.startTime?.split(':')[0] || 8),
        endHour: parseInt(batch.endTime?.split(':')[0] || 9),
        description: course?.description || '',
        capacity: batch.maxCapacity || classroom?.capacity || 0,
        currentEnrollment: batch.currentEnrollment || 0,
        status: 'scheduled',
        category: course?.category || 'general',
        color: getEventColor(course?.category || 'general'),
        dayName: dayNames[dayIndex]
      };
    });
  });

  // Convert training sessions to schedule events (secondary source)
  const sessionEvents = (Array.isArray(trainingSessions) ? trainingSessions : []).map(session => {
    // Find related data (with defensive checks)
    const course = (Array.isArray(coursesData) ? coursesData : []).find(c => c.id === session.moduleId);
    const classroom = (Array.isArray(classroomsData) ? classroomsData : []).find(c => c.id === session.locationId);
    const trainer = (Array.isArray(trainersData) ? trainersData : []).find(t => t.id === session.instructorId);

    // Convert session dates to schedule format
    const startDate = new Date(session.startDate + 'T' + session.startTime);
    const endDate = new Date(session.endDate + 'T' + session.endTime);

    return {
      id: `session-${session.id}`,
      title: session.title || course?.title || 'Formation',
      courseId: session.moduleId,
      courseName: course?.title || 'Cours non spécifié',
      courseCode: course?.courseCode || '',
      trainerId: session.instructorId,
      trainerName: trainer?.name || session.instructorName || 'Formateur non spécifié',
      classroomId: session.locationId,
      classroomName: classroom?.name || session.location || 'Salle non spécifiée',
      startDate: session.startDate,
      endDate: session.endDate,
      startTime: session.startTime,
      endTime: session.endTime,
      startDateTime: startDate,
      endDateTime: endDate,
      day: startDate.getDay(),
      startHour: startDate.getHours(),
      endHour: endDate.getHours(),
      description: session.description || course?.description || '',
      capacity: session.capacity || classroom?.capacity || 0,
      enrolledStudents: session.enrolledStudents || [],
      status: session.status || 'scheduled',
      category: session.category || course?.category || 'general',
      color: getEventColor(session.category || course?.category || 'general')
    };
  });

  // Combine all events
  const events = [...batchEvents, ...sessionEvents];

  console.log('📅 Schedule Events Debug:', {
    batchesCount: batchesData?.length || 0,
    batchEventsCount: batchEvents.length,
    sessionEventsCount: sessionEvents.length,
    totalEvents: events.length,
    sampleBatch: batchesData?.[0],
    sampleEvent: events[0]
  });

  // Get events for a specific date
  const getEventsForDate = (date) => {
    if (!date) return [];
    
    return events.filter(event => {
      // Handle both batch events and session events
      if (event.startDateTime && event.startDateTime instanceof Date) {
        // Session events have startDateTime
      const eventDate = event.startDateTime;
        try {
      return eventDate.getDate() === date.getDate() &&
             eventDate.getMonth() === date.getMonth() &&
             eventDate.getFullYear() === date.getFullYear();
        } catch (error) {
          console.warn('Invalid date in event:', event);
          return false;
        }
      } else if (event.day !== undefined && event.day !== null) {
        // Batch events use day of week
        try {
          const targetDay = date.getDay();
          return event.day === targetDay;
        } catch (error) {
          console.warn('Invalid date provided:', date);
          return false;
        }
      }
      return false;
    });
  };


  // Calculate real-time statistics based on current view
  const currentWeekDates = getCurrentWeekDates();
  
  const stats = {
    totalCourses: events.length,
    monthCourses: events.filter(event => {
      if (event.startDateTime && event.startDateTime instanceof Date) {
        // Session events with startDateTime
      const eventDate = event.startDateTime;
        try {
      return eventDate.getMonth() === currentDate.getMonth() &&
             eventDate.getFullYear() === currentDate.getFullYear();
        } catch (error) {
          return false;
        }
      } else if (event.day !== undefined) {
        // Batch events - consider all batch events as current month for now
        return true;
      }
      return false;
    }).length,
    weekCourses: events.filter(event => {
      if (event.startDateTime && event.startDateTime instanceof Date) {
        // Session events with startDateTime
      const eventDate = event.startDateTime;
        try {
      return currentWeekDates.some(weekDate => 
        eventDate.getDate() === weekDate.getDate() &&
        eventDate.getMonth() === weekDate.getMonth() &&
        eventDate.getFullYear() === weekDate.getFullYear()
      );
        } catch (error) {
          return false;
        }
      } else if (event.day !== undefined) {
        // Batch events - check if any day of current week matches
        try {
          return currentWeekDates.some(weekDate => weekDate.getDay() === event.day);
        } catch (error) {
          return false;
        }
      }
      return false;
    }).length,
    trainerCount: (Array.isArray(trainersData) ? trainersData : []).length,
    classroomCount: (Array.isArray(classroomsData) ? classroomsData : []).length
  };

  const formaLinkBtn = FORMA_LINK_BTN;
  const scheduleKpi = [
    {
      title: 'Total cours',
      value: String(stats.totalCourses),
      hint: 'Événements planifiés',
      icon: AcademicCapIcon,
      iconWrap: 'bg-forma-500/15 text-forma-700 dark:bg-forma-500/20 dark:text-forma-200',
    },
    {
      title: 'Cette semaine',
      value: String(stats.weekCourses),
      hint: 'Créneaux sur la semaine',
      icon: CalendarDaysIcon,
      iconWrap: 'bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-100',
    },
    {
      title: 'Formateurs',
      value: String(stats.trainerCount),
      hint: 'Référentiel formateurs',
      icon: UserGroupIcon,
      iconWrap: 'bg-teal-500/15 text-teal-900 dark:bg-teal-500/20 dark:text-teal-100',
    },
    {
      title: 'Salles',
      value: String(stats.classroomCount),
      hint: 'Espaces disponibles',
      icon: BuildingLibraryIcon,
      iconWrap: 'bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-100',
    },
  ];

  const scheduleViewTabs = [
    { id: 'week', name: 'Vue semaine', icon: CalendarDaysIcon },
    { id: 'day', name: 'Vue jour', icon: ClockIcon },
    { id: 'trainers', name: 'Par formateur', icon: UserGroupIcon },
    { id: 'courses', name: 'Par cours', icon: AcademicCapIcon },
    { id: 'rooms', name: 'Par salle', icon: BuildingLibraryIcon },
  ];

  // Auto-refresh when data changes from other pages
  useEffect(() => {
    console.log('Schedule data updated - Events:', events.length, 'Stats:', stats);
  }, [trainingSessions, coursesData, classroomsData, trainersData]);

  // Auto-update calendar to current date at midnight or when component mounts
  useEffect(() => {
    const updateCalendarIfNeeded = () => {
      const today = new Date();
      const isShowingCurrentMonth = currentDate.getMonth() === today.getMonth() && 
                                   currentDate.getFullYear() === today.getFullYear();
      
      // Only auto-update if we're showing a very old month (more than 6 months difference)
      const monthDifference = Math.abs(
        (today.getFullYear() - currentDate.getFullYear()) * 12 + 
        (today.getMonth() - currentDate.getMonth())
      );
      
      if (monthDifference > 6) {
        setCurrentDate(today);
      }
    };

    // Check immediately
    updateCalendarIfNeeded();

    // Set up interval to check every hour
    const interval = setInterval(updateCalendarIfNeeded, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [currentDate]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle navigation keys if not typing in an input
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          if (event.shiftKey) {
            goToPreviousYear();
          } else {
            goToPreviousMonth();
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (event.shiftKey) {
            goToNextYear();
          } else {
            goToNextMonth();
          }
          break;
        case 'Home':
          event.preventDefault();
          goToToday();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentDate]);

  // No mock data - using only real-time data

  // Fonction pour ouvrir le modal de détail d'un événement
  const openEventDetail = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
    setIsAdding(false);
  };

  // Fonction pour ouvrir le modal d'ajout d'événement
  const openAddEventModal = (day, hour) => {
    setSelectedEvent({
      day,
      startHour: hour,
      endHour: hour + 1,
    });
    setShowEventModal(true);
    setIsAdding(true);
  };

  // Fonction pour fermer le modal
  const closeEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  // Fonction pour supprimer un événement
  const handleDeleteEvent = (eventId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette session de formation ?')) {
      try {
        // Delete from both collections like in SessionsPage
        const success1 = localStorageService.deleteItem('sessions', eventId);
        const success2 = localStorageService.deleteItem('training-sessions', eventId);
        
        console.log('Delete operation success:', { sessions: success1, trainingSessions: success2 });
        
        if (success1 || success2) {
          // Invalidate and refetch all interconnected data
          queryClient.invalidateQueries('training-sessions');
          queryClient.invalidateQueries('sessions');
          queryClient.invalidateQueries('courses');
          queryClient.invalidateQueries('classrooms');
          queryClient.invalidateQueries('trainers');
          console.log('Session removed and queries invalidated');
          toast.success('Session supprimée avec succès');
          closeEventModal();
        } else {
          toast.error('Erreur lors de la suppression');
        }
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Une erreur est survenue lors de la suppression');
      }
    }
  };

  // Fonction pour éditer un événement
  const handleEditEvent = (event) => {
    // Navigate to sessions page with edit parameter for that specific session
    navigate(`/sessions?edit=${event.id}`);
  };

  // Navigation functions for interconnection with other pages
  const handleEventAction = (event, action = 'view') => {
    switch (action) {
      case 'session':
        navigate('/sessions');
        break;
      case 'course':
        navigate('/courses');
        break;
      case 'classroom':
        navigate('/classrooms');
        break;
      case 'trainer':
        navigate('/trainers');
        break;
      default:
        openEventDetail(event);
    }
  };

  // Enhanced filtering for real-time events
  const filteredEvents = events.filter(event => {
    if (!event) return false;
    
    // Search filter with expanded fields
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const title = event.title || '';
      const trainerName = event.trainerName || '';
      const classroomName = event.classroomName || '';
      const courseName = event.courseName || '';
      const description = event.description || '';
      
      return title.toLowerCase().includes(searchLower) ||
             trainerName.toLowerCase().includes(searchLower) ||
             classroomName.toLowerCase().includes(searchLower) ||
             courseName.toLowerCase().includes(searchLower) ||
             description.toLowerCase().includes(searchLower);
    }
    
    // Status and type filters
    if (filterType === 'all') return true;
    if (filterType === 'confirmed') return event.status === 'confirmed' || event.status === 'scheduled';
    if (filterType === 'pending') return event.status === 'pending';
    
    return true;
  });
  
  // Distribution des cours par jour
  const coursesByDay = days.map((day, index) => ({
    name: day,
    count: events.filter(event => event.day === index).length
  }));

  // Loading state for real-time data
  if (sessionsLoading) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white/80 px-6 py-12 dark:border-slate-700 dark:bg-slate-900/75">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-slate-900 dark:text-slate-50">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forma-500 mx-auto mb-4"></div>
            <p className="text-lg">🔄 Chargement de l'emploi du temps...</p>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">Synchronisation avec les sessions, cours, groupes, salles et formateurs</p>
          </div>
        </div>
      </div>
    );
  }

  // Rendu de l'interface
  return (
    <>
      <Routes>
        <Route path="/" element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <PageHeader
              title="Emploi du temps"
              description={`Sessions, cours, salles et formateurs interconnectés • ${events.length} événements chargés (temps réel)`}
              actions={
                <Link to="/schedule/add" className={formaLinkBtn}>
                  <PlusIcon className="h-5 w-5" />
                  <span>Nouveau cours</span>
                </Link>
              }
            />

            <UiCard padding={false} className="overflow-hidden">
              <div className="border-b border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-slate-50/80 dark:border-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
                <div className="grid gap-6 p-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
                  {scheduleKpi.map((stat, index) => (
                    <motion.div
                      key={stat.title}
                      layout
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ type: 'spring', stiffness: 380, damping: 28, delay: index * 0.05 }}
                      whileHover={{ y: -2 }}
                      className="group relative rounded-2xl border border-slate-200/80 bg-white/80 p-[1px] shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/55"
                    >
                      <div className="relative overflow-hidden rounded-[0.9375rem] bg-gradient-to-b from-white/90 to-white/35 p-4 dark:from-slate-950/95 dark:to-slate-950/65">
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-forma-200/35 to-transparent opacity-70 blur-2xl transition group-hover:opacity-100 dark:from-forma-600/25"
                        />
                        <div className="relative flex gap-3">
                          <div
                            className={clsx(
                              'relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80',
                              stat.iconWrap
                            )}
                          >
                            <stat.icon className="h-5 w-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                              {stat.title}
                            </p>
                            <p className="mt-0.5 text-2xl font-bold tabular-nums tracking-tight text-slate-900 dark:text-white">
                              {stat.value}
                            </p>
                            <p className="mt-1 border-t border-slate-100 pt-2 text-xs text-slate-500 dark:border-slate-700/70 dark:text-slate-400">
                              {stat.hint}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="border-b border-slate-200 bg-white/40 px-4 py-4 dark:border-slate-700 dark:bg-slate-900/35">
                <div className="flex gap-2 overflow-x-auto pb-1 hide-scrollbar">
                  {scheduleViewTabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={clsx(
                        'inline-flex shrink-0 items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-all',
                        activeTab === tab.id
                          ? 'scale-[1.02] bg-forma-600 text-white shadow-md shadow-forma-600/25'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                      )}
                    >
                      <tab.icon className="h-4 w-4" />
                      <span className="whitespace-nowrap">{tab.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </UiCard>

            {/* Répartition des cours par jour */}
            <UiCard padding={false} className="overflow-hidden">
              <div className="border-b border-slate-200 px-6 py-5 dark:border-slate-700">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">Répartition par jour</h3>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Charge hebdomadaire</p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7 lg:gap-4">
                  {coursesByDay.map((item) => {
                    const denom = Math.max(1, events.length);
                    const pct = (item.count / denom) * 100;
                    return (
                      <div
                        key={item.name}
                        className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-600 dark:bg-slate-800/40"
                      >
                        <div className="mb-2 flex items-center justify-between">
                          <h4 className="text-sm font-medium text-slate-900 dark:text-slate-50">
                            {item.name.substring(0, 3)}
                          </h4>
                          <div className="rounded-full bg-forma-500/90 px-2 py-0.5 text-xs font-semibold text-white shadow-sm">
                            {item.count}
                          </div>
                        </div>
                        <div className="h-2.5 w-full rounded-full bg-slate-200/90 dark:bg-slate-800/80">
                          <div
                            className="h-2.5 rounded-full bg-gradient-to-r from-forma-500 to-forma-700"
                            style={{ width: `${pct.toFixed(1)}%` }}
                          />
                        </div>
                        <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                          {pct.toFixed(1)}% des cours
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="border-t border-slate-200 bg-white/30 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/25">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="relative w-full md:max-w-md">
                    <label htmlFor="schedule-search" className="sr-only">
                      Rechercher un cours
                    </label>
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      id="schedule-search"
                      type="search"
                      autoComplete="off"
                      placeholder="Titre, formateur, salle…"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={clsx(
                        'w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500',
                        searchTerm.trim() ? 'pr-10' : 'pr-4'
                      )}
                    />
                    {searchTerm.trim() !== '' ? (
                      <button
                        type="button"
                        aria-label="Effacer"
                        onClick={() => setSearchTerm('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <FunnelIcon className="h-5 w-5 shrink-0 text-slate-500 dark:text-slate-400" />
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 md:w-auto"
                    >
                      <option value="all">Tous les cours</option>
                      <option value="confirmed">Confirmés</option>
                      <option value="pending">En attente</option>
                    </select>
                  </div>
                </div>
              </div>
            </UiCard>

            {/* Navigation Calendar Controls */}
            <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-4">
              <div className="flex items-center justify-between">
                {/* Year Navigation */}
                <div className="flex items-center space-x-2">
              <button
                    onClick={goToPreviousYear}
                className="p-2 rounded-lg bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 text-slate-900 dark:text-slate-50 transition-colors"
                    title="Année précédente (Shift+←)"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                    <ChevronLeftIcon className="h-4 w-4 -ml-2" />
                  </button>
                  <span className="text-slate-600 dark:text-slate-400 text-sm font-medium min-w-[60px] text-center">
                    {currentDate.getFullYear()}
                  </span>
                  <button
                    onClick={goToNextYear}
                    className="p-2 rounded-lg bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 text-slate-900 dark:text-slate-50 transition-colors"
                    title="Année suivante (Shift+→)"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                    <ChevronRightIcon className="h-4 w-4 -ml-2" />
                  </button>
                </div>

                {/* Month Navigation */}
                <div className="flex items-center space-x-4">
                  <button
                    onClick={goToPreviousMonth}
                    className="p-2 rounded-lg bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 text-slate-900 dark:text-slate-50 transition-colors"
                    title="Mois précédent (←)"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              
                  <div className="text-center min-w-[200px]">
                    <div className="text-slate-900 dark:text-slate-50 font-semibold text-lg capitalize">
                      {getCurrentMonthYear()}
                    </div>
                    <div className="text-slate-500 dark:text-slate-400 text-xs">
                      {stats.monthCourses} cours ce mois
                    </div>
              </div>
              
              <button
                    onClick={goToNextMonth}
                className="p-2 rounded-lg bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 text-slate-900 dark:text-slate-50 transition-colors"
                    title="Mois suivant (→)"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>

                {/* View Controls */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={goToToday}
                    className="rounded-lg bg-forma-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-forma-500 dark:text-white"
                    title="Aujourd'hui (Home)"
                  >
                    Aujourd'hui
                  </button>
                </div>
              </div>
            </div>

            {/* Dynamic Content Based on Active Tab */}
            {activeTab === 'week' ? (
              /* Month Calendar View */
              <div className="bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-600">
                <div className="p-4">
                  {/* Month View */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Day Headers */}
                    {days.map(day => (
                      <div key={day} className="p-3 text-center">
                        <div className="text-slate-600 dark:text-slate-400 text-xs uppercase tracking-wider font-medium">
                          {day.slice(0, 3)}
                        </div>
                      </div>
                    ))}
                    
                    {/* Calendar Days */}
                    {getCalendarDays().map((date, index) => {
                      const dayEvents = getEventsForDate(date);
                      const isCurrentMonthDate = isCurrentMonth(date);
                      const isTodayDate = isToday(date);
                      
                      return (
                        <div
                          key={index}
                          className={`min-h-[100px] p-2 border border-slate-200 dark:border-slate-600 rounded-lg relative cursor-pointer transition-all hover:bg-slate-100/80 dark:bg-slate-800/40 ${
                            isCurrentMonthDate ? 'bg-slate-100/80 dark:bg-slate-800/40' : 'bg-white/2'
                          } ${
                            isTodayDate ? 'bg-forma-500/15 border-forma-400 ring-1 ring-forma-400' : ''
                          }`}
                          onClick={() => {
                            setCurrentDate(new Date(date));
                          }}
                        >
                          <div className={`text-sm font-medium mb-2 ${
                            isCurrentMonthDate ? 'text-slate-900 dark:text-slate-50' : 'text-slate-400 dark:text-slate-500'
                          } ${
                            isTodayDate ? 'text-forma-700 dark:text-forma-200 font-bold' : ''
                          }`}>
                            {date.getDate()}
                          </div>
                          
                          {/* Events for this day */}
                          <div className="space-y-1">
                            {dayEvents.slice(0, 2).map((event, eventIndex) => (
                              <div
                                key={event.id}
                                className="text-xs p-1 rounded truncate cursor-pointer hover:opacity-80"
                                style={{
                                  backgroundColor: `${getEventColor(event.category)}40`,
                                  borderLeft: `2px solid ${getEventColor(event.category)}`,
                                  color: '#fff'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEventAction(event);
                                }}
                                title={`${event.title} - ${event.startTime} à ${event.endTime}`}
                              >
                                <div className="font-medium truncate">{event.title}</div>
                                <div className="text-xs opacity-80">{event.startTime}</div>
                              </div>
                            ))}
                            {dayEvents.length > 2 && (
                              <div className="text-xs text-slate-600 dark:text-slate-400 text-center py-1">
                                +{dayEvents.length - 2} autre{dayEvents.length - 2 > 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : activeTab === 'day' ? (
              /* Vue Jour - Today's Schedule */
              <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50">Vue du Jour</h3>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">{new Date().toLocaleDateString('fr-FR', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentDate(new Date(currentDate.getTime() - 24 * 60 * 60 * 1000))}
                      className="p-2 rounded-lg bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 text-slate-900 dark:text-slate-50 transition-colors"
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setCurrentDate(new Date())}
                      className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-slate-900 dark:text-slate-50 text-sm font-medium transition-colors"
                    >
                      Aujourd'hui
                    </button>
                    <button
                      onClick={() => setCurrentDate(new Date(currentDate.getTime() + 24 * 60 * 60 * 1000))}
                      className="p-2 rounded-lg bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 text-slate-900 dark:text-slate-50 transition-colors"
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                {/* Timeline for today */}
                <div className="space-y-4">
                  {hours.map(hour => {
                    const hourEvents = events.filter(event => {
                      if (!event) return false;
                      const eventDate = new Date();
                      const eventDay = eventDate.getDay();
                      const startTime = event.startTime || '00:00';
                      const endTime = event.endTime || '23:59';
                      
                      try {
                        const startHour = parseInt(startTime.split(':')[0]);
                        const endHour = parseInt(endTime.split(':')[0]);
                        return event.day === eventDay && 
                               startHour <= hour && 
                               endHour > hour;
                      } catch (error) {
                        console.warn('Invalid time format for event:', event);
                        return false;
                      }
                    });
                    
                    return (
                      <div key={hour} className="flex items-start space-x-4">
                        <div className="w-16 text-right">
                          <span className="text-slate-600 dark:text-slate-400 text-sm font-medium">{hour}:00</span>
                        </div>
                        <div className="flex-1 min-h-[60px] relative">
                          {hourEvents.length > 0 ? (
                            <div className="grid gap-2">
                              {hourEvents.map(event => (
                                <div
                                  key={event.id}
                                  className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 border-l-4 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors"
                                  style={{ borderLeftColor: getEventColor(event.category) }}
                                  onClick={() => handleEventAction(event)}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-slate-900 dark:text-slate-50 font-medium">{event.title}</h4>
                                    <span className="text-slate-600 dark:text-slate-400 text-sm">{event.startTime} - {event.endTime}</span>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                    <div className="flex items-center text-slate-600 dark:text-slate-400">
                                      <UserGroupIcon className="h-4 w-4 mr-2" />
                                      {event.trainerName}
                                    </div>
                                    <div className="flex items-center text-slate-600 dark:text-slate-400">
                                      <BuildingLibraryIcon className="h-4 w-4 mr-2" />
                                      {event.classroomName}
                                    </div>
                                    <div className="flex items-center text-slate-600 dark:text-slate-400">
                                      <AcademicCapIcon className="h-4 w-4 mr-2" />
                                      {event.courseName}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="h-full flex items-center">
                              <div className="w-full border-t border-slate-200 dark:border-slate-600 border-dashed"></div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Daily Summary */}
                <div className="mt-8 bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4">
                  <h4 className="text-slate-900 dark:text-slate-50 font-medium mb-3">Résumé du jour</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-400">{events.filter(e => e && e.day === new Date().getDay()).length}</p>
                      <p className="text-slate-600 dark:text-slate-400 text-sm">Cours total</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-400">{new Set(events.filter(e => e && e.day === new Date().getDay() && e.trainerId).map(e => e.trainerId)).size}</p>
                      <p className="text-slate-600 dark:text-slate-400 text-sm">Formateurs</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-forma-600 dark:text-forma-400">{new Set(events.filter(e => e && e.day === new Date().getDay() && e.classroomId).map(e => e.classroomId)).size}</p>
                      <p className="text-slate-600 dark:text-slate-400 text-sm">Salles utilisées</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-orange-400">{events.filter(e => e && e.day === new Date().getDay()).reduce((acc, e) => {
                        try {
                          const startTime = e.startTime || '00:00';
                          const endTime = e.endTime || '00:00';
                          const startHour = parseInt(startTime.split(':')[0]);
                          const endHour = parseInt(endTime.split(':')[0]);
                          return acc + (endHour - startHour);
                        } catch (error) {
                          return acc;
                        }
                      }, 0)}</p>
                      <p className="text-slate-600 dark:text-slate-400 text-sm">Heures total</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : activeTab === 'trainers' ? (
              /* Par Formateur View */
              <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-6">Emploi du temps par Formateur</h3>
                <div className="grid gap-6">
                  {trainersData.filter(trainer => trainer && trainer.id).map(trainer => {
                    const trainerEvents = events.filter(event => event && event.trainerId === trainer.id);
                    return (
                      <div key={trainer.id} className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="h-12 w-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                              <UserGroupIcon className="h-6 w-6 text-blue-300" />
                            </div>
                            <div>
                              <h4 className="text-slate-900 dark:text-slate-50 font-medium">{trainer.name || 'Formateur'}</h4>
                              <p className="text-slate-600 dark:text-slate-400 text-sm">{trainerEvents.length} cours cette semaine</p>
                            </div>
                          </div>
                          <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                            {trainer.status === 'active' ? 'Actif' : 'Inactif'}
                          </span>
                        </div>
                        <div className="grid gap-2">
                          {trainerEvents.length > 0 ? trainerEvents.map(event => (
                            <div key={event.id} className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-3 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors"
                                 onClick={() => handleEventAction(event)}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-slate-900 dark:text-slate-50 font-medium">{event.title || 'Cours'}</p>
                                  <p className="text-slate-600 dark:text-slate-400 text-sm">{event.classroomName || 'Salle'} • {days[event.day] || 'Jour'}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-blue-300 text-sm">{event.startTime || '00:00'} - {event.endTime || '00:00'}</p>
                                  <p className="text-slate-600 dark:text-slate-400 text-xs">{event.courseName || 'Cours'}</p>
                                </div>
                              </div>
                            </div>
                          )) : (
                            <p className="text-slate-600 dark:text-slate-400 italic">Aucun cours assigné cette semaine</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : activeTab === 'courses' ? (
              /* Par Cours View */
              <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-6">Emploi du temps par Cours</h3>
                <div className="grid gap-6">
                  {coursesData.filter(course => course && course.id).map(course => {
                    const courseEvents = events.filter(event => event && event.courseId === course.id);
                    return (
                      <div key={course.id} className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="h-12 w-12 rounded-full bg-forma-500/15 flex items-center justify-center">
                              <AcademicCapIcon className="h-6 w-6 text-forma-600 dark:text-forma-400" />
                            </div>
                            <div>
                              <h4 className="text-slate-900 dark:text-slate-50 font-medium">{course.title || 'Cours'}</h4>
                              <p className="text-slate-600 dark:text-slate-400 text-sm">{courseEvents.length} sessions cette semaine</p>
                            </div>
                          </div>
                          <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">
                            {course.duration || 'N/A'}h
                          </span>
                        </div>
                        <div className="grid gap-2">
                          {courseEvents.length > 0 ? courseEvents.map(event => (
                            <div key={event.id} className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-3 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors"
                                 onClick={() => handleEventAction(event)}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-slate-900 dark:text-slate-50 font-medium">{event.trainerName || 'Formateur'}</p>
                                  <p className="text-slate-600 dark:text-slate-400 text-sm">{event.classroomName || 'Salle'} • {days[event.day] || 'Jour'}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-forma-600 dark:text-forma-400 text-sm">{event.startTime || '00:00'} - {event.endTime || '00:00'}</p>
                                  <p className="text-slate-600 dark:text-slate-400 text-xs">Groupe {event.batchName || 'A'}</p>
                                </div>
                              </div>
                            </div>
                          )) : (
                            <p className="text-slate-600 dark:text-slate-400 italic">Aucune session planifiée cette semaine</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : activeTab === 'rooms' ? (
              /* Par Salle View */
              <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-6">Emploi du temps par Salle</h3>
                <div className="grid gap-6">
                  {classroomsData.filter(room => room && room.id).map(room => {
                    const roomEvents = events.filter(event => event && event.classroomId === room.id);
                    const occupancyRate = ((roomEvents.length / 40) * 100).toFixed(1); // Assuming 40 hours per week
                    return (
                      <div key={room.id} className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="h-12 w-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                              <BuildingLibraryIcon className="h-6 w-6 text-orange-300" />
                            </div>
                            <div>
                              <h4 className="text-slate-900 dark:text-slate-50 font-medium">{room.name || 'Salle'} {room.code ? `(${room.code})` : ''}</h4>
                              <p className="text-slate-600 dark:text-slate-400 text-sm">Capacité: {room.capacity || 'N/A'} • {roomEvents.length} sessions</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`px-3 py-1 rounded-full text-sm ${
                              room.isAvailable ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {room.isAvailable ? 'Disponible' : 'Occupée'}
                            </span>
                            <p className="text-slate-600 dark:text-slate-400 text-xs mt-1">{occupancyRate}% d'occupation</p>
                          </div>
                        </div>
                        {/* Equipment indicators */}
                        <div className="flex items-center space-x-4 mb-4">
                          {room.hasProjector && <span className="text-blue-300 text-xs">📽️ Projecteur</span>}
                          {room.hasAirConditioning && <span className="text-blue-300 text-xs">❄️ Climatisation</span>}
                          {room.hasComputers && <span className="text-blue-300 text-xs">💻 Ordinateurs</span>}
                          {room.hasWhiteboard && <span className="text-blue-300 text-xs">📋 Tableau</span>}
                        </div>
                        <div className="grid gap-2">
                          {roomEvents.length > 0 ? roomEvents.map(event => (
                            <div key={event.id} className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-3 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors"
                                 onClick={() => handleEventAction(event)}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-slate-900 dark:text-slate-50 font-medium">{event.title || 'Cours'}</p>
                                  <p className="text-slate-600 dark:text-slate-400 text-sm">{event.trainerName || 'Formateur'} • {days[event.day] || 'Jour'}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-orange-300 text-sm">{event.startTime || '00:00'} - {event.endTime || '00:00'}</p>
                                  <p className="text-slate-600 dark:text-slate-400 text-xs">{event.courseName || 'Cours'}</p>
                                </div>
                              </div>
                            </div>
                          )) : (
                            <p className="text-slate-600 dark:text-slate-400 italic">Salle libre cette semaine</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </motion.div>
        } />
        
        <Route path="/:id" element={<ScheduleEventDetailWrapper events={events} onDelete={handleDeleteEvent} onEdit={handleEditEvent} onNavigate={handleEventAction} />} />
        
        <Route path="/add" element={<div>Ajouter un nouveau cours</div>} />
        <Route path="/:id/edit" element={<div>Modifier le cours</div>} />
      </Routes>

      {/* Modal de détail d'événement */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm dark:bg-black/75 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl p-6 max-w-lg w-full"
          >
            {isAdding ? (
              <>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-6">Ajouter un cours</h2>
                <form className="space-y-4">
                  <div>
                    <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                      Cours
                    </label>
                    <select className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500">
                      <option value="">Sélectionner un cours</option>
                      <option value="C001">Introduction au Développement Web</option>
                      <option value="C002">Design Graphique avec Photoshop</option>
                      <option value="C003">Administration Linux</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                      Formateur
                    </label>
                    <select className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500">
                      <option value="">Sélectionner un formateur</option>
                      <option value="T001">Ahmed Kader</option>
                      <option value="T002">Amina Benali</option>
                      <option value="T003">Karim Ramdani</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                      Salle
                    </label>
                    <select className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500">
                      <option value="">Sélectionner une salle</option>
                      <option value="CR001">Salle Informatique 1</option>
                      <option value="CR002">Salle de cours A</option>
                      <option value="CR004">Laboratoire Réseau</option>
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                        Heure de début
                      </label>
                      <select 
                        className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                        defaultValue={selectedEvent.startHour}
                      >
                        {hours.map(h => (
                          <option key={h} value={h}>{h}:00</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                        Heure de fin
                      </label>
                      <select 
                        className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                        defaultValue={selectedEvent.endHour}
                      >
                        {hours.map(h => (
                          <option key={h} value={h}>{h}:00</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={closeEventModal}
                      className="px-4 py-2 bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors rounded-lg text-slate-900 dark:text-slate-50"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 rounded-lg text-white font-medium shadow bg-forma-600 hover:bg-forma-500 transition-all duration-200"
                    >
                      Ajouter
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">Détails du cours</h2>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditEvent(selectedEvent)}
                      className="p-2 bg-slate-100/80 dark:bg-slate-800/40 rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteEvent(selectedEvent.id)}
                      className="p-2 bg-slate-100/80 dark:bg-slate-800/40 rounded-lg text-slate-600 dark:text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 mb-4 border-l-4" style={{ borderLeftColor: selectedEvent.color }}>
                  <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">{selectedEvent.title}</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 flex items-center">
                    <CalendarDaysIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-3" />
                    <div>
                      <div className="text-slate-600 dark:text-slate-400 text-sm">Horaires</div>
                      <div className="text-slate-900 dark:text-slate-50">
                        {days[selectedEvent.day]}, {selectedEvent.startHour}:00 - {selectedEvent.endHour}:00
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 flex items-center">
                    <UserGroupIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-3" />
                    <div>
                      <div className="text-slate-600 dark:text-slate-400 text-sm">Formateur</div>
                      <div className="text-slate-900 dark:text-slate-50">
                        {selectedEvent.trainerName}
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 flex items-center">
                    <BuildingLibraryIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-3" />
                    <div>
                      <div className="text-slate-600 dark:text-slate-400 text-sm">Salle</div>
                      <div className="text-slate-900 dark:text-slate-50">
                        {selectedEvent.classroomName}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end mt-6">
                  <button
                    onClick={closeEventModal}
                    className="px-4 py-2 bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors rounded-lg text-slate-900 dark:text-slate-50"
                  >
                    Fermer
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </>
  );
};

export default SchedulePage; 